import React, { useRef, useState } from 'react';
import HoneyPot, { IHoneyPot } from '../Shared/HoneyPot';
import { ISimpleFeedbackFormApiModel } from './models/simple-feedback-form.api-model';
import { LanguageService } from '../../services/language.service';
import RadioFormGroup from '../Shared/RadioFormGroup';
import ValidationInfo from '../../validation/validation-info';
import TextFieldFormGroup from '../Shared/TextFieldFormGroup';
import LogService from '../../services/log-service/log.service';

export interface SimpleFeedbackFormProps {
  contentGuid: string;
  question: string;
  answerOptionOne: string;
  answerOptionTwo: string;
  useCloseButton: boolean;
  answerOptionOneUseComment: boolean;
  answerOptionOneCommentRequired: boolean;
  answerOptionTwoUseComment: boolean;
  answerOptionTwoCommentRequired: boolean;
  answerOptionOneText: string;
  answerOptionTwoText: string;
  submitText: string;
  feedbackSumbittedText: string;
  errorMessage?: string;
  colorTheme: string;
  className?: string;
  antiforgeryToken: string;
}

function SimpleFeedbackForm({
  contentGuid,
  question,
  answerOptionOne,
  answerOptionTwo,
  useCloseButton,
  answerOptionOneUseComment,
  answerOptionOneCommentRequired,
  answerOptionTwoUseComment,
  answerOptionTwoCommentRequired,
  answerOptionOneText,
  answerOptionTwoText,
  submitText,
  errorMessage,
  feedbackSumbittedText,
  colorTheme,
  className,
  antiforgeryToken,
}: SimpleFeedbackFormProps) {
  // === STATE ===
  const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);
  const [commentOne, setCommentOne] = useState('');
  const [commentTwo, setCommentTwo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [show, setShow] = useState(true);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // === REFS ===
  const honeyPotRef = useRef<IHoneyPot>(null!);

  // === VARIABLES ===
  const showCommentOne = selectedOption === answerOptionOne && answerOptionOneUseComment;
  const showCommentTwo = selectedOption === answerOptionTwo && answerOptionTwoUseComment;
  const isFormValid = !!selectedOption && (!showCommentOne || !answerOptionOneCommentRequired || !!commentOne) && (!showCommentTwo || !answerOptionTwoCommentRequired || !!commentTwo);

  // === EVENT HANDLERS ===
  function handleOptionChange(option: string) {
    setShowErrorMessage(false);
    setSelectedOption(option);
    if (option === answerOptionOne) {
      setCommentTwo('');
    } else {
      setCommentOne('');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isFormValid || isSubmitting || !honeyPotRef.current.isValid()) return;

    setIsSubmitting(true);
    setShowErrorMessage(false);

    try {
      const requestData: ISimpleFeedbackFormApiModel = {
        $type: 'SimpleFeedback',
        blockContentGuid: contentGuid,
        url: window.location.href,
        answer: selectedOption || '',
        answerOptionOneComment: showCommentOne ? commentOne : undefined,
        answerOptionTwoComment: showCommentTwo ? commentTwo : undefined,
      };

      const formData = new FormData();
      formData.append('feedbackForm', JSON.stringify(requestData));

      const response = await fetch(`/api/forms/submit?language=${LanguageService.getCurrentLanguage()}`, {
        method: 'POST',
        headers: { 'X-XSRF-TOKEN': antiforgeryToken },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSubmitted(true);
    } catch (error) {
      LogService.logError(error);
      setShowErrorMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={`simple-feedback-form p-4 position-relative ${colorTheme} ${show ? '' : 'd-none'} ${className}`}>
      {useCloseButton && <button type="button" className="btn-close position-absolute p-2 top-0 end-0" aria-label="Close" onClick={() => setShow(false)} />}

      {isSubmitted && <p className="mb-0">{feedbackSumbittedText}</p>}
      {!isSubmitted && (
        <form onSubmit={handleSubmit}>
          <fieldset disabled={isSubmitting}>
            <legend className="form-label h4">{question}</legend>

            <div className="mb-3">
              <RadioFormGroup
                options={[
                  { value: answerOptionOne, text: answerOptionOne },
                  { value: answerOptionTwo, text: answerOptionTwo },
                ]}
                rowLayout={true}
                state={{ value: selectedOption ?? '', validationInfo: new ValidationInfo() }}
                onChange={handleOptionChange}
              />
            </div>

            {showCommentOne && (
              <div className="mb-3">
                <TextFieldFormGroup
                  labelText={answerOptionOneText}
                  required={answerOptionOneCommentRequired}
                  inputId={`comment-one-${contentGuid}`}
                  autocomplete="off"
                  renderAsTextArea={true}
                  rows={2}
                  state={{ value: commentOne, validationInfo: new ValidationInfo() }}
                  onChange={(value) => {
                    setCommentOne(value);
                  }}
                  onKeyUp={() => {
                    setCommentOne(commentOne);
                  }}
                />
              </div>
            )}

            {showCommentTwo && (
              <div className="mb-3">
                <TextFieldFormGroup
                  labelText={answerOptionTwoText}
                  required={answerOptionTwoCommentRequired}
                  inputId={`comment-two-${contentGuid}`}
                  autocomplete="off"
                  renderAsTextArea={true}
                  rows={2}
                  state={{ value: commentTwo, validationInfo: new ValidationInfo() }}
                  onChange={(value) => {
                    setCommentTwo(value);
                  }}
                  onKeyUp={() => {
                    setCommentTwo(commentTwo);
                  }}
                />
              </div>
            )}

            {showErrorMessage && (
              <div className="alert alert-danger" role="alert">
                {errorMessage}
              </div>
            )}
            <HoneyPot ref={honeyPotRef} />
            <button type="submit" className="btn btn-primary w-100" disabled={!isFormValid || isSubmitting}>
              {submitText}
            </button>
          </fieldset>
        </form>
      )}
    </div>
  );
}

export default SimpleFeedbackForm;
